import React, { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import cn from 'classnames';
import { FiSearch, FiX } from 'react-icons/fi';
import AccessUserRight from 'components/AccessUserRight';
import Button from 'components/UI/Buttons/buttons';
import ElementsNone from 'components/ElementsNone';
import TableFilter from 'components/UI/TableFilter';
import { RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import PreloaderList from 'components/Preloaders/List';
import NavigationHeader from 'components/NavigationHeader';
import { Element, scroller } from 'react-scroll';
import css from './styles.module.scss';
import { ReactComponent as FilterSvg } from '../../contracts/ui/icons/filters.svg';
import { useHighlights } from '../../../utils/useLastIdHelpers/useLastIdCounteragent';
import Item from './Item';
import { useCounteragentsData } from '../model/hooks/useCounteragentsData';

type Props = RouteComponentProps;

const Counteragents: FC<Props> = ({ ...params }) => {
  const { t } = useTranslation();
  
  const {
    currentTab,
    search,
    toggleSearch,
    onOpenSearch,
    onChangeSearch,
    onCloseSearch,
    isThemeExtended,
    isThemeFull,
    isLoading,
    couneragentView,
    handleChangeTab,
    searchRef,
    searchWrapperRef
  } = useCounteragentsData();

  const lastId = useHighlights();

  useEffect(() => {
    if (lastId) {
      setTimeout(() => {
        scroller.scrollTo(lastId, {
          duration: 0,
          delay: 0,
          smooth: false,
          offset: -400,
        });
      }, 1000);
    }
  }, [lastId, currentTab]);

  return (
    <div className={css.contractors}>
      <Helmet>
        <title>{t(`ord.сounterparties_title`)}</title>
      </Helmet>
      <NavigationHeader>
        <button
          type="button"
          className={cn('tabs__item', {
            isActive: currentTab === 'ACTIVE',
          })}
          onClick={() => handleChangeTab('ACTIVE')}
        >
          {t(`agencies_page.agencies_active`)}
        </button>
        <button
          type="button"
          className={cn('tabs__item', {
            isActive: currentTab === 'ON_APPROVAL',
          })}
          onClick={() => handleChangeTab('ON_APPROVAL')}
        >
          {t(`ord.contracts.in_moderation`)}
        </button>
        <button
          type="button"
          className={cn('tabs__item', {
            isActive: currentTab === 'DELETED',
          })}
          onClick={() => handleChangeTab('DELETED')}
          disabled={false}
        >
          {t(`campaigns_page.campaigns_black_header_main.del`)}
        </button>
        <button
          type="button"
          className={cn('tabs__item', {
            isActive: currentTab === 'ALL',
          })}
          onClick={() => handleChangeTab('ALL')}
          disabled={false}
        >
          {t(`targetings.all`)}
        </button>
      </NavigationHeader>
      <div
        className={cn(css.header, {
          isToggleSearch: toggleSearch,
        })}
      >
        <div
          className={cn('container resized', {
            extend: isThemeExtended,
            full: isThemeFull,
          })}
        >
          <div className={css.status}>
            <h1 className={css.title}>{t(`ord.сounterparties_title`)}</h1>
            <div className={css.actions}>
              <div
                ref={searchWrapperRef}
                className={cn(css.search, {
                  isActive: toggleSearch,
                })}
              >
                <div className={css.icon} onClick={onOpenSearch}>
                  <FiSearch size={24} />
                </div>
                <div className={css.input}>
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder={t(`ord.сounterparties_search`)}
                    value={search}
                    onChange={onChangeSearch}
                    ref={searchRef}
                  />
                </div>

                <div className={css.close} onClick={onCloseSearch}>
                  <FiX size={24} />
                </div>
              </div>
              <AccessUserRight>
                {({ isDisable }) => (
                  <Button
                    title={t(`ord.counterparte_create`)}
                    buttonAttributes={{
                      disabled: isDisable,
                      style: {
                        height: 40,
                      },
                      onClick: () =>
                        params.history.push({
                          pathname: `counteragent/create`,
                        }),
                    }}
                  />
                )}
              </AccessUserRight>
            </div>
          </div>
        </div>
      </div>
      {couneragentView?.length === 0 ? (
        <ElementsNone type="counteragents" search={!!search} status="" />
      ) : (
        <div className={css.content}>
          <div
            className={cn('container resized', {
              extend: isThemeExtended,
              full: isThemeFull,
            })}
          >
            <div className={css.table}>
              <div className={css.table_header}>
                <div className={css.filter_header}>
                  <FilterSvg />
                </div>
                <TableFilter
                  data=""
                  title={t(`ord.name_of_legal`)}
                  handleSort={() => null}
                  sort={null}
                  activeKey={null}
                />
                <TableFilter
                  data=""
                  title={t(`ord.name_for_export`)}
                  handleSort={() => null}
                  sort={null}
                  activeKey={null}
                />
                <TableFilter
                  data=""
                  title={t(`ord.synonyms_name`)}
                  handleSort={() => null}
                  sort={null}
                  activeKey={null}
                />
                <TableFilter
                  data=""
                  title={t(`ord.inn`)}
                  handleSort={() => null}
                  sort={null}
                  activeKey={null}
                />
                <TableFilter
                  data=""
                  title={t(`ord.title`)}
                  handleSort={() => null}
                  sort={null}
                  activeKey={null}
                />
              </div>
              {isLoading && <PreloaderList type="counteragents" />}
              {!isLoading && (
                <div className={css.table_body}>
                  {couneragentView?.map((counteragent) => (
                    <Element name={counteragent.uuid} key={counteragent.uuid}>
                      <Item
                        counteragentData={counteragent}
                        isLastEdited={lastId === counteragent.uuid}
                      />
                    </Element>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Counteragents;
